// ── EPP (Elementos de Protección Personal) ──────────────────────────────────────
// EPP no es una entidad en la base: un acta de EPP es una fila más de
// `mantenimiento.documentos`, normalizada al tipo `certificado_seguridad`
// (zc_backend/lib/tipoDocumento.js). Para poder mostrar el historial de entregas de una
// persona hay que reconocerlas por su nombre, que en producción viene con 50 variantes
// ("Registro de Entrega de EPP", "Entrega EPP", "Uso y Mantención de EPP", ...).
//
// Las heurísticas se portan desde zc_backend/scripts/export_epp_personas.js, que es el
// único código EPP-first ya validado contra los datos reales. Mantener ambos en sintonía:
// si cambia una, cambiar la otra.
//
// NO se reutiliza `grupoDeDocumento` de documentoGrupos.ts: ese asigna el PRIMER grupo que
// calza y 'entrada_faena' va antes que 'operacional', así que clasificaría "Entrega de EPP"
// como entrada a faena. Es otra pregunta y otra respuesta.

/** Un REGISTRO acredita la entrega; una ENTREGA sin registro es más débil; una
 *  CAPACITACIÓN (curso de uso y mantención) NO acredita ninguna entrega. */
export type ClaseEPP = 'registro' | 'entrega' | 'capacitacion';

/** Estado global de EPP de una persona, mismas 4 categorías que el Excel del backend. */
export type EstadoEPP = 'con_registro' | 'entrega_sin_registro' | 'solo_capacitacion' | 'sin_epp';

export const CLASE_EPP_LABEL: Record<ClaseEPP, string> = {
  registro: 'Registro',
  entrega: 'Entrega',
  capacitacion: 'Capacitación',
};

/** Nombre y tipo con los que se preconfigura el modal de subida, para que las actas dejen
 *  de entrar con nombres inconsistentes. El tipo debe ser el mismo al que normaliza
 *  zc_backend/lib/tipoDocumento.js; usar otro rompería el match de prerrequisitos. */
export const EPP_NOMBRE_SUGERIDO = 'Registro de Entrega de EPP';
export const EPP_TIPO_SUGERIDO = 'certificado_seguridad';

// Minúsculas, sin acentos, sin puntuación. Además une las secuencias de letras sueltas
// que deja la puntuación, para que "E.P.P." → "e p p" → "epp" y el \bepp\b la encuentre.
const norm = (s?: string): string =>
  String(s || '')
    .toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\b(?:[a-z] )+[a-z]\b/g, (m) => m.replace(/ /g, ''));

// Texto contra el que se hace el match. `tipo_documento` queda FUERA a propósito: en esta
// base es texto libre y termina guardando cualquier cosa, así que arrastra documentos
// ajenos. Casos reales en producción: "Control de riesgos para la radiación Ultravioleta."
// y "Examen Altura Física", ambos con tipo "Curso Uso y Mantención de EPP" — mirando el
// tipo entraban al panel como capacitación de EPP. Ningún EPP real necesita el tipo para
// ser reconocido: las 50 variantes de producción se identifican por su nombre.
const textoDocumento = (doc: any): string =>
  norm([doc?.nombre_documento, doc?.nombre_original, doc?.nombre_archivo]
    .filter(Boolean).join(' '));

// Solo el nombre propio del documento, con el mismo COALESCE que usa el script del backend.
const nombreDocumento = (doc: any): string =>
  norm(doc?.nombre_documento || doc?.nombre_original || doc?.nombre_archivo);

const ES_EPP = /(\bepp\b|elementos? de proteccion|proteccion personal)/;
const ES_REGISTRO = /registro/;
const ES_ENTREGA = /(entrega|recepcion)/;
// "mantencio" sin la n final no es un descuido: es un typo real en los nombres cargados
// ("Uso y mantencio de los EPP"), y el prefijo cubre las dos escrituras.
const ES_CAPACITACION = /(curso|capacitacion|diploma|uso y mantencio|uso de)/;

/** true si el documento es un EPP en cualquiera de sus formas. */
export const esEPP = (doc: any): boolean => {
  const hay = textoDocumento(doc);
  return !!hay && ES_EPP.test(hay);
};

/**
 * Clase del documento de EPP, o null si no es un EPP.
 * Jerarquía (la del script del backend): registro > entrega > capacitación, y todo lo que
 * es EPP pero no calza en ninguna se cuenta como entrega.
 */
export const claseEPP = (doc: any): ClaseEPP | null => {
  if (!esEPP(doc)) return null;
  const hay = textoDocumento(doc);
  if (ES_REGISTRO.test(hay)) return 'registro';
  if (ES_ENTREGA.test(hay) || nombreDocumento(doc) === 'epp') return 'entrega';
  if (ES_CAPACITACION.test(hay)) return 'capacitacion';
  return 'entrega';
};

/** Fecha con la que se ordena la línea de tiempo: la de emisión si el acta la trae,
 *  si no la de subida. Las que no tienen ninguna caen al final. */
const fechaEPP = (doc: any): number => {
  const raw = doc?.fecha_emision || doc?.fecha_subida;
  const t = raw ? new Date(raw).getTime() : NaN;
  return Number.isNaN(t) ? -Infinity : t;
};

/**
 * Documentos de EPP de una persona, del más reciente al más antiguo.
 * El desempate por id es necesario: el backend ordena sin criterio único
 * (routes/documentos.js `ORDER BY fecha_subida DESC, nombre_documento`), así que sin esto
 * dos actas del mismo día pueden alternar de orden entre recargas.
 */
export const documentosEPP = (docs: any[]): any[] =>
  (docs || [])
    .filter(esEPP)
    .sort((a, b) => (fechaEPP(b) - fechaEPP(a)) || (Number(b?.id ?? 0) - Number(a?.id ?? 0)));

/** Estado global de EPP de la persona, para el badge del encabezado. */
export const estadoEPPPersona = (docs: any[]): EstadoEPP => {
  const clases = new Set((docs || []).map(claseEPP).filter(Boolean) as ClaseEPP[]);
  if (clases.has('registro')) return 'con_registro';
  if (clases.has('entrega')) return 'entrega_sin_registro';
  if (clases.has('capacitacion')) return 'solo_capacitacion';
  return 'sin_epp';
};

export const ESTADO_EPP_LABEL: Record<EstadoEPP, string> = {
  con_registro: 'Con registro de entrega',
  entrega_sin_registro: 'Entrega sin registro',
  solo_capacitacion: 'Solo capacitación — no acredita la entrega',
  sin_epp: 'Sin documentos de EPP',
};
